import { useState, useContext, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';

import { DataContext } from "providers/DataProvider";
import { ME_QUERY, GROUP_QUERY, GROUP_DELETION_MUTATION, GROUP_INVITATION_ACCEPTANCE_MUTATION, INVITATION_MUTATION } from "graphql/APICalls";
import { ActivityList } from "components/ActivityList";
import { MapContext } from "providers/MapProvider";
import { useDialogState, DialogBox, DialogBoxContext } from "components/Modals";

import 'styles/GroupPage.css';

export default function GroupPage(){

  const navigate = useNavigate();

  const { me } = useContext(DataContext);
  const { updateLocation } = useContext(MapContext);
  const { groupId, groupShortId } = useParams();
  const groupQuery = useQuery(GROUP_QUERY, {variables: {id: groupId, shortId: groupShortId}});
  const [sendAcceptGroupInvitationMutation] = useMutation(GROUP_INVITATION_ACCEPTANCE_MUTATION);

  const group = useMemo(() => groupQuery.data?.getGroup, [groupQuery]);

  function handleJoinGroup(e){
    e.preventDefault();
    sendAcceptGroupInvitationMutation({
      variables: { groupId: group?.id },
      onCompleted: () => updateLocation(),
      onError: error => alert(error),
      refetchQueries: [ { query: ME_QUERY }, { query: GROUP_QUERY, variables: {id: groupId, shortId: groupShortId} } ]
    })
  };

  const deleteGroupDialogState = useDialogState();
    
  return (
    <>
    <DialogBox {...{dialogState: deleteGroupDialogState}}>
      <DeleteGroupDialogBody {...{group}}/>
    </DialogBox>
    <div className="auto-scrollbar">
    <div className="centerer group-page-overlay">
      <div className='group-page-data'>
        
        <div className="group-page-title-buttons-backdrop"/>
        
        <label className="group-page-title">{group?.name  || '\u00A0'}</label>

        <div className="group-buttons">
          {
            group?.creator.id === me.id &&
            <button className="group-delete-button" onClick={()=>{deleteGroupDialogState.setVisible(true)}}/>
          }
          {
            group?.status === 'invited' &&
            <button className="group-join-button" onClick={handleJoinGroup}/>
          }
        </div>

        <label className="group-page-description">
          {group?.description || <span style={{color: 'gray'}}>(No description)</span>}
        </label>

        <div className="members-area creator">
          <label className="members-label">Creator</label>
          <div className="members-display">
            { (!group) ? null :
              <button className="member-button joined" key={group?.creator.id} onClick = {()=>{navigate(`/u/${group?.creator.username}`)}}>
                <div className="member-pic" style={{backgroundImage: `url(${group?.creator.picture.imageSrc})`}}/>
                <div>{group?.creator.name}</div>
              </button>
            }
          </div>
        </div>

        <div className="members-area members">
          <label className="members-label">Members</label>
          <div className="members-display">
            {group?.memberships && [...group.memberships].sort((a,b)=>(a.status <= b.status ? 1 : -1)).map(
              ({user: member, status}) =>
                <button className={`member-button ${status}`} key={member.id} onClick = {()=>{navigate(`/u/${member.username}`)}}>
                  <div className="member-pic" style={{backgroundImage: `url(${member.picture?.imageSrc})`}}/>
                  <div>{member.name}</div>
                </button>)
            }
            {group?.creator.id === me.id ? <AddMemberButton groupId={group?.id}/> : null}
          </div>
        </div>
        
        <div className="group-activities">
          <h3>Group activities</h3>
          
          {
            group?.status === 'joined' ?
            <ActivityList activities={group?.activities} popping={true}/> :
            group?.status === 'invited' ?
            <label>You will be able to see this group's activities once you join.</label> :
            <label>You can't see activities in this group since you have not been invited.</label>
          }
        </div>

      </div>
    </div>
    </div>
    </>
  )

}

function AddMemberButton({groupId}) {

  // TODO: make it possible to send that request by hitting Enter on the keyboard
  // TODO: collapse this module once the request has been sent
  // TODO: alert the user if the user doesn't exist, has already been invited, etc.

  const [inputMode, setInputMode] = useState(false);
  const [memberHandle, setMemberHandle] = useState(null);

  const [sendInvitationMutation] = useMutation(INVITATION_MUTATION);

  function handleClick(){
    if (inputMode) {
      if (memberHandle) {
        sendInvitationMutation({
          variables: {groupId, inviteeUsername: memberHandle},
          refetchQueries: [ { query: GROUP_QUERY, variables: { id: groupId } } ]
        })
      }
      else { setInputMode(false) }
    }
    else { setInputMode(true) }
  }

  return (
    <div className="add-member-module">
      <input type="email" className={inputMode ? "shown" : "hidden"} onChange={e=>setMemberHandle(e.target.value.toLowerCase())} placeholder="Username"/>
      <div className={"button-icon " + (inputMode && !memberHandle ? "x" : "plus")} onClick={handleClick}/>
      <label className={inputMode ? "hidden" : "shown"} onClick={handleClick}>{'Add\u00A0member'}</label>
    </div>
  )
};

function DeleteGroupDialogBody({group}) {

  const navigate = useNavigate();

  const dialogState = useContext(DialogBoxContext);

  const [sendGroupDeletionMutation] = useMutation(GROUP_DELETION_MUTATION);

  return (
    <div>
      <p>Are you sure you want to delete this group?</p>
      <button onClick={()=>{
          sendGroupDeletionMutation({
            variables: { groupId: group?.id },
            onCompleted: data => {if (data.deleteGroup) {navigate('/')}},
            refetchQueries: [ { query: ME_QUERY } ]
          })
        }}>Yes</button>
      <button onClick={()=>{dialogState.setVisible(false)}}>No</button>
    </div>
  )
}